import { useState, useContext } from "react";
import { BackNavigation } from "../partials/BackNavigation";
import { Divider } from "../../../../../Resources/Scripts/_Shared/elements/Divider";
import { BookingFlowContext } from "../BookingFlowContext";
import {
    base64ToFile,
    fileToBase64,
    hasSalesOrderAttachment,
    isSalesOrderAttachmentRequired,
} from "../util";
import Instructions from "../partials/Instructions";
import { products } from "../Endpoints";

const DocumentUpload = () => {
    const { bookingFlowContext, setBookingFlowContext } = useContext(BookingFlowContext);

    const existingDocument = bookingFlowContext.availableSteps.documentUpload || {};
    const orderId = bookingFlowContext.availableSteps.onlineBooking?.order?.orderId;
    const localization = bookingFlowContext.localization?.uploadDocument || {};
    const globalButtons = bookingFlowContext.localization?.global?.buttons || {};

    const [selectedFile, setSelectedFile] = useState(() => {
        if (existingDocument.selectedFile) {
            const base64String = existingDocument?.selectedFile;
            const file = base64ToFile(
                base64String,
                existingDocument?.fileMeta?.name,
                existingDocument.fileMeta?.mimeType
            );

            if (file && file.name && file.size) {
                return file;
            }

            const meta = existingDocument?.fileMeta;
            if (meta?.name) {
                return { fileName: meta.fileName, sizeBytes: meta.sizeBytes };
            }
        }

        return null;
    });

    const [error, setError] = useState("");

    const attachmentMissing =
        isSalesOrderAttachmentRequired(bookingFlowContext) &&
        !hasSalesOrderAttachment(bookingFlowContext);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        const maxSize = 5 * 1024 * 1024;

        const allowedTypes = [
            "application/pdf",
            "text/plain",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "image/png",
            "image/jpeg",
        ];

        if (!file) {
            return;
        }

        if (!allowedTypes.includes(file.type)) {
            setError(
                "Please upload a valid PDF, TXT, DOC, DOCX, PNG, JPG, or JPEG file."
            );
            e.target.value = null;
            return;
        }

        if (file.size > maxSize) {
            setError("File size must be 5MB or less.");
            e.target.value = null;
            return;
        }

        if (!orderId) {
            setError("Upload failed.");
            e.target.value = null;
            return;
        }

        try {
            const prepareResponse = await products.prepareSalesOrderAttachmentUpload({
                OrderId: orderId,
                FileName: file.name,
                MimeType: file.type,
            });

            if (prepareResponse.status >= 400 || !prepareResponse.body?.UploadUrl) {
                console.error("Prepare upload failed", prepareResponse);
                setError("Upload failed.");
                e.target.value = null;
                return;
            }

            const { UploadUrl: uploadUrl, AttachmentId: attachmentId } = prepareResponse.body;

            const s3Response = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });

            if (!s3Response.ok) {
                console.error("S3 upload failed", s3Response.status);
                setError("Upload failed.");
                e.target.value = null;
                return;
            }

            const linkResponse = await products.linkSalesOrderAttachments({
                OrderId: orderId,
                AttachmentIds: [attachmentId],
            });

            if (linkResponse.status >= 400) {
                console.error("Link attachment failed", linkResponse);
                setError("Upload failed.");
                e.target.value = null;
                return;
            }

            const attachmentRef = {
                Uid: attachmentId,
                FileName: file.name,
                MimeType: file.type,
                SizeBytes: file.size,
            };

            setSelectedFile(file);
            await addFile(file, attachmentRef);
            setError("");
        } catch (err) {
            console.error("Upload failed", err);
            setError("Upload failed.");
        }
    };

    const finalizeFile = () => {
        if (attachmentMissing) {
            return;
        }

        setBookingFlowContext({
            ...bookingFlowContext,
            activeStep: bookingFlowContext.availableSteps.orderOverview.index,
        });
    };

    const addFile = async (file, metaData) => {
        const base64 = await fileToBase64(file);
        setBookingFlowContext({
            ...bookingFlowContext,
            availableSteps: {
                ...bookingFlowContext.availableSteps,
                documentUpload: {
                    ...bookingFlowContext.availableSteps.documentUpload,
                    selectedFile: base64,
                    fileMeta: {
                        uid: metaData.Uid,
                        name: metaData.FileName,
                        sizeBytes: metaData.SizeBytes,
                        mimeType: metaData.MimeType,
                    },
                },
            },
        });
    };

    const approvedInstructions = [
        localization.instructions?.approved?.step1,
        localization.instructions?.approved?.step2,
        localization.instructions?.approved?.step3,
    ];

    const requiredInstructions = [
        localization.instructions?.required?.step1,
        localization.instructions?.required?.step2,
        localization.instructions?.required?.step3,
    ];

    return (
        <div className="bookingflowpage__app bookingflowpage__app--unreservedproduct bookingflowpage__app--documentupload">
            <BackNavigation />
            <div className="single-step single-step--booking">
                <h2>
                    {localization.title}
                </h2>
                <div className="bookingflowpage__add-upload-document-container">
                    <h3 className="left bookingflowpage__add-upload-document__title">
                        {localization.description}
                    </h3>

                    <Instructions
                        title={localization.instructions?.approved?.title}
                        instructions={approvedInstructions}
                        titleLeft={true}
                    />

                    <Instructions
                        title={localization.instructions?.required?.title}
                        instructions={requiredInstructions}
                        titleLeft={true}
                    />

                    <div className="bookingflowpage__add-upload-document__file">
                        <input
                            type="file"
                            id="file-upload"
                            accept=".pdf,.txt,.doc,.docx,.png,.jpeg,.jpg"
                            onChange={handleFileChange}
                            className="file-upload-hidden"
                        />
                        <label htmlFor="file-upload" className="upload-button">
                            {localization.upload}
                        </label>

                        {selectedFile && (
                            <p className="margin">
                                <strong>Selected:</strong> {selectedFile.name}
                                {!selectedFile.size && (
                                    <span className="warning">
                                        {" "}
                                        (Please re-upload this file)
                                    </span>
                                )}
                            </p>
                        )}
                    </div>

                    {error && (
                        <p className="bookingflowpage__add-upload-document__error">
                            {error}
                        </p>
                    )}

                    {attachmentMissing && (
                        <p className="bookingflowpage__add-upload-document__error">
                            {localization.requiredError}
                        </p>
                    )}

                    <Divider />

                    <button
                        className="single-step__button single-step__button--next"
                        disabled={attachmentMissing}
                        onClick={finalizeFile}
                    >
                        {globalButtons.nextStep || "Next"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DocumentUpload;
